import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
// MediaSFU colour tokens (--ms-*). Global because dashboard components that use
// them also render on public pages; light values stay gated on data-surface.
import './styles/mediasfuTheme.css';
import './styles/performance-lite.css';
import App, { preloadLanding } from './App';
import reportWebVitals from './reportWebVitals';
import Modal from 'react-modal';
import { applyPerformanceMode } from './utils/performanceMode';
import { holdPrerenderedPage, noteAppCommitted } from './utils/prerenderHold';
import { installSyntheticMediaFromUrl } from './components/Navigation/MainNavigation/SolutionCapsules/capsuleSyntheticMedia';
import { installTurnstileRetry } from './utils/turnstile';

// Before first paint, so constrained devices never render the expensive pass.
applyPerformanceMode();

// Synthetic camera/mic/screen, opt-in via `?fakeMedia=1` and only on localhost
// or a Dev build. Installed here rather than inside a route because it patches
// `navigator.mediaDevices`, and the SDK may reach for media before any showcase
// component has mounted — a later install would miss that first call.
installSyntheticMediaFromUrl();

// Off unless REACT_APP_TURNSTILE_SITE_KEY is set: a Turnstile 429 on a human-only action shows a quick check and retries once.
installTurnstileRetry();


const rootElement = document.getElementById('root');

// Saved into every prerendered page (react-snap keeps <html> attributes). On
// the live site it says "this content was already painted": entrance
// animations that start from opacity 0 and count-ups skip themselves, instead
// of hiding the page for a moment and replaying it.
// The theme it was painted in is saved too (see prerenderHold).
if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
  document.documentElement.setAttribute('data-mediasfu-prerendered', '');
  document.documentElement.setAttribute('data-mediasfu-prerender-theme', document.documentElement.getAttribute('data-theme') || 'dark');
}

// react-modal marks this element aria-hidden while a dialog is open, so
// screen readers cannot wander into the page behind it. Set once here;
// individual modals no longer need to opt out with ariaHideApp={false}.
if (rootElement) {
  Modal.setAppElement(rootElement);
}
const hasPrerenderRouteMismatch = document.documentElement.hasAttribute('data-mediasfu-route-mismatch');

if (hasPrerenderRouteMismatch) {
  rootElement.replaceChildren();
} else {
  holdPrerenderedPage(rootElement);
}

const AppBootstrap = () => {
  React.useLayoutEffect(() => {
    if (hasPrerenderRouteMismatch) {
      rootElement.style.removeProperty('visibility');
      document.documentElement.removeAttribute('data-mediasfu-route-mismatch');
    }
    // A held prerendered page reveals #root itself once real content commits.
    noteAppCommitted();
  }, []);

  return <App />;
};

const root = ReactDOM.createRoot(rootElement);
const renderApp = () => root.render(
  <React.StrictMode>
    <AppBootstrap />
  </React.StrictMode>
);

// The landing's code loads first, so the static copy goes straight to the real
// page with no loading screen between them. On a very slow link the wait ends
// and the loading screen shows as on any other route.
const LANDING_FIRST_RENDER_WAIT_MS = 3000;
if (['/', '/home'].includes(window.location.pathname) && !hasPrerenderRouteMismatch) {
  Promise.race([
    preloadLanding().catch(() => {}),
    new Promise((resolve) => window.setTimeout(resolve, LANDING_FIRST_RENDER_WAIT_MS)),
  ]).then(renderApp);
} else {
  renderApp();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
